import { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Linkedin, Twitter, Link2, Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { CtaButton } from "@/components/site/CtaButton";
import { Seo } from "@/components/Seo";
import { ARTICLES } from "@/data/articles";

const ShareRow = ({ title }) => {
  const [copied, setCopied] = useState(false);
  const url = window.location.href;
  const text = encodeURIComponent(`${title} — The AI Doctor`);
  const encUrl = encodeURIComponent(url);
  const iconCls =
    "w-11 h-11 border border-white/10 flex items-center justify-center text-[#a7a7a2] hover:text-[#5CCBF5] hover:border-[#5CCBF5]/40 transition-colors duration-300";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="border border-white/10 p-6 flex flex-wrap items-center justify-between gap-5" data-testid="article-share">
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#5c5c58]">Share this read</p>
      <div className="flex items-center gap-3">
        <a href={`https://twitter.com/intent/tweet?text=${text}&url=${encUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X" data-testid="article-share-x" className={iconCls}>
          <Twitter size={16} />
        </a>
        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" data-testid="article-share-linkedin" className={iconCls}>
          <Linkedin size={16} />
        </a>
        <button type="button" onClick={copy} data-testid="article-share-copy" className="h-11 px-5 border border-white/10 flex items-center gap-2.5 font-mono text-[10px] tracking-[0.18em] uppercase text-[#a7a7a2] hover:text-[#5CCBF5] hover:border-[#5CCBF5]/40 transition-colors duration-300">
          {copied ? <Check size={16} className="text-[#5CCBF5]" /> : <Link2 size={16} />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
};

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/insights" replace />;

  return (
    <div data-testid="article-page">
      <Seo title={`${article.title} | The AI Doctor`} description={article.excerpt} />
      <article className="shell pt-40 lg:pt-52 pb-32 max-w-3xl">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#5CCBF5]">
            {article.category} · {article.minutes} min read
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h1 className="display mt-6 text-[#f5f3ee] text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {article.title}
          </h1>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-8 text-[#a7a7a2] text-xl leading-relaxed border-l-2 border-[#5CCBF5]/50 pl-6">
            {article.excerpt}
          </p>
        </Reveal>

        <div className="mt-14 space-y-8">
          {article.body.map((b, i) =>
            b.h ? (
              <Reveal key={i} delay={0.03}>
                <h2 className="display text-2xl sm:text-3xl text-[#f5f3ee] pt-6">{b.h}</h2>
              </Reveal>
            ) : (
              <Reveal key={i} delay={0.03}>
                <p className="text-[#c9c9c4] text-lg leading-[1.8]">{b.p}</p>
              </Reveal>
            )
          )}
        </div>

        <Reveal delay={0.05} className="mt-14">
          <ShareRow title={article.title} />
        </Reveal>

        <Reveal delay={0.05} className="mt-16 border border-[#5CCBF5]/30 bg-[#131C31] p-8 lg:p-10">
          <p className="eyebrow mb-4">The Next Step</p>
          <p className="display text-2xl sm:text-3xl text-[#f5f3ee] leading-snug max-w-xl">
            Reading about symptoms is useful. Getting yours diagnosed is faster.
          </p>
          <div className="mt-8">
            <CtaButton to="/apply" testid="article-cta">Get Your Free AI Check-Up</CtaButton>
          </div>
        </Reveal>

        <Reveal delay={0.05} className="mt-12">
          <CtaButton to="/insights" variant="ghost" testid="article-back" arrow="←">All Insights</CtaButton>
        </Reveal>
      </article>
    </div>
  );
}
